import { Component, Host, h, Element, State, Prop, Watch } from '@stencil/core';
import { JsonViewer } from '@alenaksu/json-viewer/dist/JsonViewer.js';

// Use an aha-scoped, more esoteric name to prevent registry conflicts if this
// web components bundle is included on other pages as `json-viewer` is a
// somewhat generic name
if (!customElements.get('aha-imported-json-viewer')) {
  customElements.define('aha-imported-json-viewer', JsonViewer);
}

// Custom type definitions for JsonViewer
interface IJsonViewer extends HTMLElement {
  data: any;
  expandAll: () => void;
  collapseAll: () => void;
  expand: (glob: string) => void;
  search: (query: string) => { next: () => void };
}

/**
 * A custom JSON viewer component that displays JSON data with expandable/collapsible nodes,
 * search functionality, layered navigation, and the ability to highlight specific information.
 *
 * @prop {string | object} [jsonData] - The JSON data to be displayed in the viewer. If not provided, the component will attempt to use the inner text content.
 *
 * Pass in the json via json-data:
 * @example
 * <aha-json-viewer
 *   json-data='{"auto_send_changes": true, "auto_import": false, "jql_filter": "project = FOO"}'
 * </aha-json-viewer>
 */
@Component({
  tag: 'aha-json-viewer',
  styleUrl: 'aha-json-viewer.css',
  shadow: false,
  scoped: true,
})
export class AhaJsonViewer {
  @Element() host!: HTMLAhaJsonViewerElement;
  @State() currentSearch: ReturnType<IJsonViewer['search']> | null = null;
  @State() currentDepth: number = 0;
  @Prop() jsonData?: string | object;

  private viewer: JsonViewer | null = null;
  private searchInput: HTMLInputElement | null = null;

  componentDidLoad() {
    this.viewer = this.host.querySelector('aha-imported-json-viewer') as unknown as JsonViewer;
    this.searchInput = this.host.querySelector('input[placeholder="Search"]');

    this.updateJsonData();
  }

  @Watch('jsonData')
  jsonDataChanged() {
    this.updateJsonData();
  }

  updateJsonData() {
    if (this.viewer) {
      let data: any;
      if (this.jsonData !== undefined) {
        data = typeof this.jsonData === 'string' ? JSON.parse(this.jsonData) : this.jsonData;
      } else {
        const slotContent = this.host.textContent?.trim();
        if (slotContent) {
          try {
            data = JSON.parse(slotContent);
          } catch (e) {
            console.error('Invalid JSON in slot content:', e);
          }
        }
      }
      if (data) {
        this.jsonData = data;
      }
    }
  }

  handleExpandAllClick = (event: Event) => {
    event.preventDefault();
    this.viewer?.expandAll();
    this.currentDepth = Infinity;
  };

  handleCollapseAllClick = (event: Event) => {
    event.preventDefault();
    this.viewer?.collapseAll();
    this.currentDepth = 0;
  };

  handleOpenToLayerClick = (event: Event) => {
    event.preventDefault();
    this.currentDepth++;
    this.openToLayer(this.currentDepth);
  };

  handleCollapseLayerClick = (event: Event) => {
    event.preventDefault();
    if (this.currentDepth > 0) {
      this.currentDepth--;
      this.openToLayer(this.currentDepth);
    }
  };

  openToLayer(depth: number) {
    if (this.viewer) {
      this.viewer.expand(this.getGlobPattern(depth));
    }
  }

  getGlobPattern(depth: number) {
    return Array(depth).fill('*').join('.');
  }

  handleSearchInput = () => {
    if (this.viewer && this.searchInput) {
      this.currentSearch = this.viewer.search(this.searchInput.value);
    }
  };

  handleSearchKeyUp = (event: KeyboardEvent) => {
    if (event.key === 'Enter' && this.currentSearch) {
      this.currentSearch.next();
    }
  };

  handleSearchNextClick = () => {
    this.currentSearch?.next();
  };

  render() {
    return (
      <Host>
        <div class="actions-toolbar">
          <div class="main-actions">
            <button onClick={this.handleExpandAllClick}>
              <i class="fa-solid fa-expand"></i>
              Expand All
            </button>
            <button onClick={this.handleCollapseAllClick}>
              <i class="fa-solid fa-compress"></i>
              Collapse All
            </button>
            <button onClick={this.handleOpenToLayerClick}>
              <i class="fa-solid fa-layer-group"></i>
              Expand Layer
            </button>
            <button onClick={this.handleCollapseLayerClick}>
              <i class="fa-solid fa-layer-group fa-flip-vertical"></i>
              Collapse Layer
            </button>
          </div>
          <div class="search-filter-actions">
            <div class="search-container">
              <label htmlFor="json-search">
                Search
                <i class="fa-regular fa-circle-question"></i>
                <aha-tooltip>
                  Enter search term and press Enter or click Next to find matches
                </aha-tooltip>
              </label>
              <input
                id="json-search"
                type="text"
                placeholder="Search"
                onInput={this.handleSearchInput}
                onKeyUp={this.handleSearchKeyUp}
              />
              <button onClick={this.handleSearchNextClick}>
                <i class="fa-solid fa-chevron-right"></i>
                Next
              </button>
            </div>
          </div>
        </div>

        <aha-imported-json-viewer>{this.jsonData}</aha-imported-json-viewer>
      </Host>
    );
  }
}
